import { FC, useRef } from 'react';
import DropdownMenuItem from '../atoms/DropdownMenuItem';
import { CustomDialogBox } from './CustomDialogBox';
import useClickOutsideHook from '../../hooks/useClickOutsideHook';

interface CustomDropdownProps {
  isOpen: boolean;
  className?: string;
  children?: React.ReactNode;
  fullWidth?: boolean | false;
  dropdownItems: DropdownItem[] | [];
  dropdownArrow?: React.ReactNode;
  setIsOpen?: (isOpen: boolean) => void;
}

interface Dialog {
  title: string;
  description: string;
  leftBtn: string;
  rightBtn: string;
  leftClick?: () => void;
  rightClick?: () => void;
}

export interface DropdownItem {
  title: string;
  icon: React.ReactNode;
  dialog?: Dialog;
  contentStyle?: React.HTMLAttributes<HTMLDivElement> | string;
  onClick?: (item: DropdownItem) => void;
  disabled?: boolean;
}

interface ArrowDropdownProps {
  isOpen?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export const ArrowDropdown: React.FC<ArrowDropdownProps> = ({
  isOpen,
  style = {},
  className = '',
}) => {
  return (
    <div
      style={{ transform: 'rotate(360deg)', right: 0, ...style }}
      className={`tooltip-arrow ${isOpen ? 'active' : ''} ${className}`}
    />
  );
};

const CustomDropdown: FC<CustomDropdownProps> = ({
  isOpen = false,
  dropdownItems,
  setIsOpen,
  className,
  children,
  dropdownArrow,
  fullWidth,
}: CustomDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutsideHook({
    ref: dropdownRef,
    clickOutside(isClickOutside) {
      setIsOpen?.(!isClickOutside);
    },
  });

  return (
    <div
      className={`relative ${fullWidth ? 'w-full' : ''} shrink`}
      ref={dropdownRef}
    >
      {children}

      {isOpen && dropdownArrow}
      {isOpen && (
        <div
          className={`${className} absolute right-0 z-50 mt-1 w-48 rounded-md border border-line bg-white py-1 shadow-lg`}
        >
          {dropdownItems.map((e) =>
            e.dialog ? (
              <CustomDialogBox
                key={e.title}
                title={e.dialog.title}
                enabled={e.disabled !== undefined ? !e.disabled : true}
                description={e.dialog.description}
                leftButtonText={e.dialog.leftBtn}
                rightButtonText={e.dialog.rightBtn}
                onLeftClick={() => {
                  e.dialog?.leftClick?.();
                }}
                onRightClick={async () => {
                  e.dialog?.rightClick?.();
                }}
                trigger={
                  <DropdownMenuItem
                    className={`${e.contentStyle ?? ''} ${e.disabled === true ? 'cursor-not-allowed opacity-50' : ''}`}
                    onClick={async (_) => {
                      if (e.disabled !== undefined && e.disabled === true)
                        return;
                      e.onClick?.(e);
                    }}
                  >
                    {e.icon}
                    <span>{e.title}</span>
                  </DropdownMenuItem>
                }
              />
            ) : (
              // A stable key: a new one on each render replaced the entry, so a tap
              // landing while the owner re-rendered hit a removed element.
              <DropdownMenuItem
                key={e.title}
                className={`${e.contentStyle ?? ''}`}
                onClick={async (ee) => {
                  ee.stopPropagation();
                  e.onClick?.(e);
                }}
              >
                {e.icon}
                <span>{e.title}</span>
              </DropdownMenuItem>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
